import React, { useState } from 'react';
import {
  Alert,
  CircularProgress,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fade,
  IconButton,
  MenuItem,
  SelectChangeEvent,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import CustomTextField from '@shared/components/forms/theme-elements/CustomTextField';
import CustomSelect from '@shared/components/forms/theme-elements/CustomSelect';
import { IconChevronDown } from '@shared/assets/images/svgs';
import { useStyles } from '../styles';
import { useCreateCampaign } from '@features/Admin/Campaign/hooks/useCreateCampaign';
import { CampaignCreationFields } from '@shared/types/admin/campaign';
import Spinner from '@shared/components/spinner/Spinner';
import { CampaignTypeEnum } from '@shared/constants/campaign.constants';
import { CampaignType } from '@shared/services/apiService/apiService';
import { capitalize } from 'lodash';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

interface NewCampaignDialogProps {
  showDialogFlag: boolean;
  handleClose: (value: React.SetStateAction<boolean>) => void;
  showDialog: (value: React.SetStateAction<boolean>) => void;
}

export const NewCampaignDialog: React.FC<NewCampaignDialogProps> = ({
  showDialogFlag,
  handleClose,
  showDialog,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();

  const [campaignName, setCampaignName] = useState<string>('');
  const [campaignType, setCampaignType] = useState<CampaignType | ''>('');
  const [showValidationSummary, setShowValidationSummary] = useState(false);
  const [reservedLimit, setReservedLimit] = useState<number | undefined>(undefined);

  const { onSubmit, setValue, reset, control, isLoading, responseLoadingFlag } =
    useCreateCampaign();
  const isSubmitting = isLoading || responseLoadingFlag;

  const handleOnChangeInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    setValue(name as CampaignCreationFields, value, {
      shouldDirty: true,
      shouldValidate: true,
    });

    if (name === 'title') {
      setCampaignName(value);
    }

    if (showValidationSummary) {
      setShowValidationSummary(false);
    }
  };

  const handleCampaignTypeChange = (event: SelectChangeEvent) => {
    const { name, value } = event.target;
    setCampaignType(value as CampaignType | '');

    setValue(name as CampaignCreationFields, value as CampaignType, {
      shouldDirty: true,
      shouldValidate: true,
    });

    if (showValidationSummary) {
      setShowValidationSummary(false);
    }
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;

    if (!campaignName || !campaignType) {
      setShowValidationSummary(true);
      return;
    } else {
      try {
        await onSubmit();
        resetModal();
      } catch {}
    }
  };

  const resetModal = () => {
    setCampaignName('');
    setCampaignType('');
    setShowValidationSummary(false);
    setReservedLimit(undefined);
    reset({
      title: '',
      type: undefined as unknown as CampaignType,
      reservedLimit: undefined,
    });
    showDialog(false);
  };

  const hasCampaignNameError = showValidationSummary && !campaignName;
  const hasCampaignTypeError = showValidationSummary && !campaignType;
  const missingFields: string[] = [];

  if (hasCampaignNameError) {
    missingFields.push(t('campaignPage.validation.fields.campaignName'));
  }
  if (hasCampaignTypeError) {
    missingFields.push(t('campaignPage.validation.fields.campaignType'));
  }

  return (
    <Dialog
      open={showDialogFlag}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      // TODO: "backdropFilter" may not work in firefox
      sx={{
        backdropFilter: 'blur(5px)',
        '& .MuiDialog-paper': {
          borderRadius: '12px',
          maxWidth: '560px',
        },
      }}>
      <DialogTitle
        fontSize="24px"
        fontWeight={600}
        lineHeight={1.2}
        id="alert-dialog-title"
        sx={{
          padding: '24px 24px 8px 24px',
        }}>
        {t('adminMainPage.operation.addCampaign')}
      </DialogTitle>
      <>
        <DialogContent
          sx={{
            padding: '0 24px',
            position: 'relative',
          }}>
          <Fade in={isSubmitting} unmountOnExit>
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 2,
                borderRadius: '8px',
                backgroundColor: 'rgba(255, 255, 255, 0.6)',
                backdropFilter: 'blur(1px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              <Spinner />
            </Box>
          </Fade>
          <Fade in={!isSubmitting} timeout={220}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.25, pt: 1 }}>
              {showValidationSummary && (
                <Alert
                  severity="error"
                  sx={{
                    alignItems: 'center',
                    py: 0.75,
                    px: 1,
                    borderRadius: '8px',
                    '& .MuiAlert-message': { width: '100%' },
                  }}>
                  <Typography variant="body2" fontWeight={600} sx={{ mb: 0.25 }}>
                    {t('campaignPage.validation.summaryTitle')}
                  </Typography>
                  <Typography variant="caption" sx={{ lineHeight: 1.4 }}>
                    {t('campaignPage.validation.summaryMissingFields', {
                      fields: missingFields.join(', '),
                    })}
                  </Typography>
                </Alert>
              )}
              <Box>
                <Typography
                  variant="subtitle1"
                  fontWeight={500}
                  component="label"
                  htmlFor="campaign-title"
                  sx={{ mb: 1, display: 'block' }}>
                  {t('campaignPage.campaignName')}
                </Typography>
                <CustomTextField
                  control={control}
                  name="title"
                  onChange={handleOnChangeInput}
                  value={campaignName}
                  id="campaign-title"
                  variant="outlined"
                  placeholder={t('campaignPage.placeholders.campaignName')}
                  sx={{
                    '#campaign-title::placeholder': {
                      color: theme.palette.extendedGrey?.neutral,
                      opacity: 1,
                    },
                    '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
                      borderColor: hasCampaignNameError ? theme.palette.error.main : undefined,
                    },
                  }}
                  fullWidth
                  required
                />
              </Box>
              <Box>
                <Typography
                  variant="subtitle1"
                  fontWeight={500}
                  component="label"
                  htmlFor="campaign-type-id"
                  sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: '6px' }}>
                  {t('campaignPage.campaignType')}
                  <Tooltip
                    arrow
                    placement="right-start"
                    enterTouchDelay={0}
                    slotProps={{
                      tooltip: {
                        sx: {
                          maxWidth: 320,
                          padding: '10px 12px',
                        },
                      },
                    }}
                    title={
                      <>
                        <Typography component="p" fontWeight={600} fontSize="12px" sx={{ mb: 0.5 }}>
                          {t('campaignPage.help.campaignTypeTitle')}
                        </Typography>
                        <Typography component="p" fontSize="12px" sx={{ lineHeight: 1.4 }}>
                          {t('campaignPage.help.inboundDescription')}
                        </Typography>
                        <Typography component="p" fontSize="12px" sx={{ lineHeight: 1.4, mt: 0.5 }}>
                          {t('campaignPage.help.outboundDescription')}
                        </Typography>
                      </>
                    }>
                    <IconButton
                      size="small"
                      sx={{
                        padding: 0,
                        color: theme.palette.text.secondary,
                        fontSize: '16px',
                        transform: 'translateY(1px)',
                      }}
                      aria-label={t('campaignPage.help.campaignTypeAriaLabel')}>
                      <HelpOutlineIcon fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                </Typography>
                <CustomSelect
                  control={control}
                  labelId="campaign-type"
                  name="type"
                  onChange={handleCampaignTypeChange}
                  id="campaign-type-id"
                  disabled={false}
                  value={campaignType}
                  IconComponent={IconChevronDown}
                  className={classes.campaign_type_select}
                  displayEmpty
                  required
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: hasCampaignTypeError ? theme.palette.error.main : undefined,
                    },
                  }}
                  renderValue={(value: string) => {
                    if (!value) {
                      return (
                        <Typography color={theme.palette.extendedGrey?.neutral}>
                          {t('campaignPage.placeholders.campaignType')}
                        </Typography>
                      );
                    }

                    return <>{capitalize(value)}</>;
                  }}>
                  <MenuItem key={CampaignTypeEnum.INBOUND} value={CampaignTypeEnum.INBOUND}>
                    {capitalize(CampaignTypeEnum.INBOUND)}
                  </MenuItem>
                  <MenuItem key={CampaignTypeEnum.OUTBOUND} value={CampaignTypeEnum.OUTBOUND}>
                    {capitalize(CampaignTypeEnum.OUTBOUND)}
                  </MenuItem>
                </CustomSelect>
              </Box>
              <Box>
                <Typography
                  variant="subtitle1"
                  fontWeight={500}
                  component="label"
                  htmlFor="campaign-reserved-limit"
                  sx={{ mb: 1, display: 'block' }}>
                  {t('campaignPage.reservedLimit')}
                </Typography>
                <CustomTextField
                  control={control}
                  name="reservedLimit"
                  id="campaign-reserved-limit"
                  type="number"
                  inputProps={{ min: 1 }}
                  variant="outlined"
                  placeholder={t('campaignPage.placeholders.reservedLimit')}
                  value={reservedLimit ?? ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const val = e.target.value;
                    const num = val === '' ? undefined : Math.max(1, Number(val));
                    setReservedLimit(num);
                    setValue('reservedLimit', num, {
                      shouldDirty: true,
                      shouldValidate: true,
                    });
                  }}
                  sx={{
                    '#campaign-reserved-limit::placeholder': {
                      color: theme.palette.extendedGrey?.neutral,
                      opacity: 1,
                    },
                  }}
                  fullWidth
                />
              </Box>
            </Box>
          </Fade>
        </DialogContent>
        <DialogActions
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            padding: '24px',
          }}>
          <Button
            color="primary"
            variant="contained"
            onClick={handleSubmit}
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress color="inherit" size={16} /> : undefined}
            sx={{ minWidth: 92, height: 44, borderRadius: '10px' }}>
            {t('adminMainPage.operation.add')}
          </Button>
          <Button
            disabled={isSubmitting}
            sx={{ minWidth: 92, height: 44, borderRadius: '10px' }}
            onClick={() => {
              showDialog(false);
              resetModal();
            }}>
            {t('adminMainPage.operation.cancel')}
          </Button>
        </DialogActions>
      </>
    </Dialog>
  );
};
